import { useCallback, useEffect, useRef, useState } from 'react';
import type { SessionData, TrackCorner, TrackGate } from '@tla/types';
import { getChannel, interpolateNaN, projectLocal, unprojectLocal } from '@tla/core';
import { useWorkspaceStore } from '../store/workspaceStore';
import { loadSession } from '../session/sessionCache';
import {
  centroidOf,
  drawGateLine,
  drawSectorGates,
  nearestByDistance,
  outwardLabelPos,
  perpendicularGate,
  satelliteUrl,
  sectorCountForTrack,
  type Geo,
} from './map/trackDraw';

/** A locally-edited corner row (id is stable across edits). */
interface Row extends TrackCorner {}

/**
 * A locally-edited sector gate. During editing a gate is pinned to a reference
 * racing-line sample (`idx`); its physical GPS endpoints are derived on save so
 * the gate stays in the same place for every lap on the track.
 */
interface SectorEdit {
  id: string;
  idx: number;
}

/** The reference lap's racing line, projected to local metres with distances. */
interface Spline {
  /** Projected local coordinates [x, y] (metres) per sample. */
  pts: [number, number][];
  /** Distance from lap start (metres) per sample. */
  distance: Float64Array;
  /** Total lap distance in metres. */
  lapDistance: number;
  /** Projection centre [lon, lat] the local frame is built around. */
  centre: [number, number];
  /** Geographic bounds (with margin) matching the projected trace. */
  geo: Geo;
}

/** A pending corner selection: entry picked, exit optionally picked. */
interface Pending {
  startIdx: number;
  endIdx: number | null;
}

/** Fit parameters mapping local metres to canvas pixels (uniform, y-flipped). */
interface Fit {
  scale: number;
  ox: number;
  oy: number;
  minX: number;
  minY: number;
  height: number;
}

const CANVAS_HEIGHT = 460;
const CANVAS_PAD = 26;

function makeId(prefix: string): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? `${prefix}_${crypto.randomUUID().slice(0, 8)}`
    : `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

/** Project the reference lap's GPS trace onto a local metric frame. */
function buildSpline(session: SessionData, lapNumber: number): Spline | null {
  const lap = session.laps.find((l) => l.number === lapNumber);
  const lat = getChannel(session, 'latitude');
  const lon = getChannel(session, 'longitude');
  if (!lap || !lat || !lon) return null;

  const { startIdx, endIdx } = lap;
  const latV = interpolateNaN(lat.samples.slice(startIdx, endIdx + 1));
  const lonV = interpolateNaN(lon.samples.slice(startIdx, endIdx + 1));
  const n = latV.length;
  if (n < 2) return null;

  let cLon = 0;
  let cLat = 0;
  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;
  for (let i = 0; i < n; i++) {
    cLon += lonV[i];
    cLat += latV[i];
    if (lonV[i] < minLon) minLon = lonV[i];
    if (lonV[i] > maxLon) maxLon = lonV[i];
    if (latV[i] < minLat) minLat = latV[i];
    if (latV[i] > maxLat) maxLat = latV[i];
  }
  const centre: [number, number] = [cLon / n, cLat / n];

  const pts: [number, number][] = new Array(n);
  for (let i = 0; i < n; i++) pts[i] = projectLocal([lonV[i], latV[i]], centre);

  const distance = new Float64Array(n);
  const dStart = session.distance[startIdx];
  for (let i = 0; i < n; i++) distance[i] = session.distance[startIdx + i] - dStart;

  // Match the 6% margin TrackMap uses so the imagery frames the racing line.
  const m = 0.06;
  const mLon = (maxLon - minLon) * m;
  const mLat = (maxLat - minLat) * m;
  const geo: Geo = {
    minLon: minLon - mLon,
    maxLon: maxLon + mLon,
    minLat: minLat - mLat,
    maxLat: maxLat + mLat,
  };

  return { pts, distance, lapDistance: distance[n - 1], centre, geo };
}

/** Nearest spline sample index to a projected local point. */
function nearestIdxToLocal(spline: Spline, local: [number, number]): number {
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < spline.pts.length; i++) {
    const dx = spline.pts[i][0] - local[0];
    const dy = spline.pts[i][1] - local[1];
    const d = dx * dx + dy * dy;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/**
 * Modal to edit the current track's corners and sector gates.
 *
 * Corners: click the entry then exit directly on the racing line; the corner is
 * anchored at its physical apex (GPS) and remembers its entry/exit span, so the
 * marker never drifts between laps.
 *
 * Sectors: click the racing line to drop a sector gate, drag it to fine-tune,
 * remove it from the list. Gates are stored as physical GPS line segments and
 * are shared by every lap on the track, so sector times are computed by actual
 * gate crossings rather than an even distance split.
 */
export function TrackEditor(props: { onClose: () => void }): React.ReactElement {
  const trackId = useWorkspaceStore((s) => s.trackId);
  const track = useWorkspaceStore((s) => (s.trackId ? s.tracks[s.trackId] : null));
  const referenceLap = useWorkspaceStore((s) => s.referenceLap);
  const saveTrackConfig = useWorkspaceStore((s) => s.saveTrackConfig);

  const [mode, setMode] = useState<'corners' | 'sectors'>('corners');
  const [rows, setRows] = useState<Row[]>(() => (track?.corners ?? []).map((c) => ({ ...c })));
  const [sectorEdits, setSectorEdits] = useState<SectorEdit[]>([]);
  const [spline, setSpline] = useState<Spline | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [name, setName] = useState('');
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [drag, setDrag] = useState<{ kind: 'sector' | 'label'; id: string } | null>(null);
  const [cursor, setCursor] = useState<'crosshair' | 'grab' | 'grabbing'>('crosshair');
  const [saving, setSaving] = useState(false);
  const sectorCount = sectorCountForTrack(track);

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const fitRef = useRef<Fit | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const satRef = useRef<HTMLImageElement | null>(null);
  const sectorsInitRef = useRef(false);
  const suppressClickRef = useRef(false);

  // Load the reference lap's GPS trace and project it once.
  useEffect(() => {
    let cancelled = false;
    if (!referenceLap) {
      setSpline(null);
      return;
    }
    void loadSession(referenceLap.sessionId).then((session) => {
      if (cancelled) return;
      setSpline(buildSpline(session, referenceLap.lapNumber));
    });
    return () => {
      cancelled = true;
    };
  }, [referenceLap]);

  // Fetch satellite imagery for the projected trace (mirrors the track map).
  useEffect(() => {
    satRef.current = null;
    if (!spline) return;
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      satRef.current = img;
      drawRef.current?.();
    };
    img.onerror = () => {
      if (!cancelled) satRef.current = null;
    };
    img.src = satelliteUrl(spline.geo);
    return () => {
      cancelled = true;
    };
  }, [spline]);

  // Seed the editable sector gates once the reference spline is available. When
  // the track already has saved GPS gates we pin each to its nearest racing-line
  // sample. Otherwise we seed the default equal-distance gates so the user can
  // simply drag them into place (they become real GPS gates on save) instead of
  // having to add every gate from scratch.
  useEffect(() => {
    if (!spline || sectorsInitRef.current) return;
    sectorsInitRef.current = true;
    const gates = track?.sectors ?? [];
    if (gates.length > 0) {
      setSectorEdits(
        gates.map((g) => {
          const mid: [number, number] = [(g.a[0] + g.b[0]) / 2, (g.a[1] + g.b[1]) / 2];
          return { id: g.id, idx: nearestIdxToLocal(spline, projectLocal(mid, spline.centre)) };
        }),
      );
    } else {
      // Interior boundaries for the default sector count (S/F excluded).
      const seeded: SectorEdit[] = [];
      for (let s = 1; s < sectorCount; s++) {
        const dTarget = (spline.lapDistance * s) / sectorCount;
        seeded.push({ id: makeId('sec'), idx: nearestByDistance(spline.distance, dTarget) });
      }
      setSectorEdits(seeded);
    }
  }, [spline, track, sectorCount]);

  const lapDistance = spline?.lapDistance ?? track?.length ?? 0;

  const distAt = useCallback(
    (idx: number): number => (spline ? spline.distance[idx] : 0),
    [spline],
  );

  /** Redraw the racing line, existing corners, and the pending selection. */
  const draw = useCallback((): void => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap || !spline) return;
    const dpr = window.devicePixelRatio || 1;
    const cssW = wrap.clientWidth || 800;
    const cssH = CANVAS_HEIGHT;
    if (canvas.width !== Math.round(cssW * dpr) || canvas.height !== Math.round(cssH * dpr)) {
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);
    ctx.fillStyle = '#0c1118';
    ctx.fillRect(0, 0, cssW, cssH);

    // Uniform fit of the projected trace into the padded canvas (y flipped).
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const [x, y] of spline.pts) {
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    const spanX = maxX - minX || 1;
    const spanY = maxY - minY || 1;
    const scale = Math.min((cssW - 2 * CANVAS_PAD) / spanX, (cssH - 2 * CANVAS_PAD) / spanY);
    const ox = (cssW - spanX * scale) / 2;
    const oy = (cssH - spanY * scale) / 2;
    const fit: Fit = { scale, ox, oy, minX, minY, height: cssH };
    fitRef.current = fit;
    const fx = (x: number): number => fit.ox + (x - fit.minX) * fit.scale;
    const fy = (y: number): number => fit.height - fit.oy - (y - fit.minY) * fit.scale;

    // Satellite imagery underlay (same proxy + alignment as the track map). The
    // geographic bbox maps to a projected rect because projectLocal is linear.
    const sat = satRef.current;
    if (sat) {
      const [sxMin, syMin] = projectLocal([spline.geo.minLon, spline.geo.minLat], spline.centre);
      const [sxMax, syMax] = projectLocal([spline.geo.maxLon, spline.geo.maxLat], spline.centre);
      const dx = fx(Math.min(sxMin, sxMax));
      const dyTop = fy(Math.max(syMin, syMax)); // north edge → smaller screen y
      const dW = Math.abs(sxMax - sxMin) * scale;
      const dH = Math.abs(syMax - syMin) * scale;
      ctx.globalAlpha = 0.9;
      ctx.drawImage(sat, dx, dyTop, dW, dH);
      ctx.globalAlpha = 1;
      // Subtle dark wash so the racing line stays legible over any imagery.
      ctx.fillStyle = 'rgba(6,10,15,0.22)';
      ctx.fillRect(dx, dyTop, dW, dH);
    }

    // Base racing line (casing + core).
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#3a4655';
    ctx.lineWidth = 6;
    ctx.beginPath();
    spline.pts.forEach(([x, y], i) => {
      const px = fx(x);
      const py = fy(y);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    });
    ctx.stroke();
    ctx.strokeStyle = '#6b7d92';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Sector gates. When gates are defined we draw them at their real
    // racing-line positions (S/F is always the lap boundary); otherwise show the
    // equal-distance fallback so the user has a reference while placing the first
    // gate. In corner-editing mode the gates are dimmed as they aren't editable
    // there.
    const lwId = (px: number): number => px;
    if (sectorEdits.length > 0) {
      ctx.save();
      if (mode === 'corners') ctx.globalAlpha = 0.45;
      const sf = perpendicularGate(spline.pts, 0);
      drawGateLine(ctx, sf.a, sf.b, 'S/F', true, fx, fy, lwId);
      [...sectorEdits]
        .sort((a, b) => a.idx - b.idx)
        .forEach((se, i) => {
          const g = perpendicularGate(spline.pts, se.idx);
          drawGateLine(ctx, g.a, g.b, `S${i + 2}`, false, fx, fy, lwId);
          if (mode === 'sectors') {
            // Draggable handle at the gate's racing-line anchor.
            const [hx, hy] = spline.pts[se.idx];
            ctx.fillStyle = drag?.id === se.id ? '#ffb454' : 'rgba(255,214,64,0.9)';
            ctx.beginPath();
            ctx.arc(fx(hx), fy(hy), drag?.id === se.id ? 6 : 4.5, 0, Math.PI * 2);
            ctx.fill();
            ctx.lineWidth = 1.5;
            ctx.strokeStyle = '#04121f';
            ctx.stroke();
          }
        });
      ctx.restore();
    } else {
      drawSectorGates(
        ctx,
        { pts: spline.pts, distance: spline.distance, lapDistance: spline.lapDistance },
        sectorCount,
        fx,
        fy,
        lwId,
        scale,
      );
    }

    /** Highlight the racing-line span between two indices. */
    const strokeSpan = (a: number, b: number, colour: string, width: number): void => {
      const lo = Math.min(a, b);
      const hi = Math.max(a, b);
      ctx.strokeStyle = colour;
      ctx.lineWidth = width;
      ctx.beginPath();
      for (let i = lo; i <= hi; i++) {
        const [x, y] = spline.pts[i];
        const px = fx(x);
        const py = fy(y);
        if (i === lo) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.stroke();
    };

    // Existing corners: highlight their span (or point), mark the apex, and draw
    // the name label just outside the track. Labels are draggable (connector line
    // links each label to its apex) and default to a position pushed radially
    // outward from the track centroid so they never overlap the racing line.
    if (mode === 'corners') {
      const centroid = centroidOf(spline.pts);
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';
      for (const r of rows) {
        const apexLocal = r.apex
          ? projectLocal(r.apex, spline.centre)
          : spline.pts[nearestByDistance(spline.distance, r.distance)];
        if (r.startDistance != null && r.endDistance != null) {
          const a = nearestByDistance(spline.distance, r.startDistance);
          const b = nearestByDistance(spline.distance, r.endDistance);
          strokeSpan(a, b, '#7fe3ff', 5);
        }
        const px = fx(apexLocal[0]);
        const py = fy(apexLocal[1]);
        ctx.fillStyle = '#7fe3ff';
        ctx.beginPath();
        ctx.arc(px, py, 4, 0, Math.PI * 2);
        ctx.fill();
        if (r.name) {
          const labelLocal = r.label
            ? projectLocal(r.label, spline.centre)
            : outwardLabelPos(apexLocal, centroid);
          const lx = fx(labelLocal[0]);
          const ly = fy(labelLocal[1]);
          // Connector from apex to label.
          ctx.strokeStyle = 'rgba(127,227,255,0.5)';
          ctx.lineWidth = 1;
          ctx.setLineDash([3, 3]);
          ctx.beginPath();
          ctx.moveTo(px, py);
          ctx.lineTo(lx, ly);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.font = '12px system-ui, sans-serif';
          // Pill background so the label reads over any imagery.
          const tw = ctx.measureText(r.name).width;
          ctx.fillStyle = drag?.id === r.id ? 'rgba(40,70,95,0.95)' : 'rgba(4,18,31,0.8)';
          ctx.beginPath();
          ctx.roundRect(lx - tw / 2 - 5, ly - 9, tw + 10, 18, 5);
          ctx.fill();
          ctx.strokeStyle = 'rgba(127,227,255,0.5)';
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.fillStyle = '#dff6ff';
          ctx.fillText(r.name, lx, ly);
        }
      }
      ctx.textAlign = 'start';

      // Pending selection: entry (green), span, exit (orange).
      if (pending) {
        if (pending.endIdx != null) strokeSpan(pending.startIdx, pending.endIdx, '#5ce08a', 5);
        const drawMarker = (idx: number, colour: string): void => {
          const [x, y] = spline.pts[idx];
          ctx.fillStyle = colour;
          ctx.beginPath();
          ctx.arc(fx(x), fy(y), 6, 0, Math.PI * 2);
          ctx.fill();
          ctx.lineWidth = 2;
          ctx.strokeStyle = '#04121f';
          ctx.stroke();
        };
        drawMarker(pending.startIdx, '#5ce08a');
        if (pending.endIdx != null) drawMarker(pending.endIdx, '#ffb454');
      }
    }

    // Hover indicator.
    if (hoverIdx != null && hoverIdx < spline.pts.length) {
      const [x, y] = spline.pts[hoverIdx];
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(fx(x), fy(y), 8, 0, Math.PI * 2);
      ctx.stroke();
    }
  }, [spline, rows, pending, hoverIdx, sectorCount, mode, sectorEdits, drag]);

  // Keep a ref to the latest draw so async loads (satellite) can redraw.
  const drawRef = useRef(draw);
  useEffect(() => {
    drawRef.current = draw;
    draw();
  }, [draw]);

  // Redraw on container resize.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const ro = new ResizeObserver(() => draw());
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [draw]);

  /** Nearest spline index to a canvas mouse event, or null if no fit yet. */
  const indexAtEvent = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>): number | null => {
      const fit = fitRef.current;
      const canvas = canvasRef.current;
      if (!fit || !canvas || !spline) return null;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      let best = 0;
      let bestD = Infinity;
      for (let i = 0; i < spline.pts.length; i++) {
        const [x, y] = spline.pts[i];
        const px = fit.ox + (x - fit.minX) * fit.scale;
        const py = fit.height - fit.oy - (y - fit.minY) * fit.scale;
        const d = (px - mx) ** 2 + (py - my) ** 2;
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      }
      return best;
    },
    [spline],
  );

  /** Nearest existing sector gate to a mouse event, within a grab radius. */
  const gateAtEvent = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>): SectorEdit | null => {
      const fit = fitRef.current;
      const canvas = canvasRef.current;
      if (!fit || !canvas || !spline || sectorEdits.length === 0) return null;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      let best: SectorEdit | null = null;
      let bestD = 16 * 16; // grab radius in px²
      for (const se of sectorEdits) {
        const [x, y] = spline.pts[se.idx];
        const px = fit.ox + (x - fit.minX) * fit.scale;
        const py = fit.height - fit.oy - (y - fit.minY) * fit.scale;
        const d = (px - mx) ** 2 + (py - my) ** 2;
        if (d < bestD) {
          bestD = d;
          best = se;
        }
      }
      return best;
    },
    [spline, sectorEdits],
  );

  /** Convert a canvas mouse event to local-metre coordinates. */
  const localAtEvent = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>): [number, number] | null => {
      const fit = fitRef.current;
      const canvas = canvasRef.current;
      if (!fit || !canvas) return null;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const x = fit.minX + (mx - fit.ox) / fit.scale;
      const y = fit.minY + (fit.height - fit.oy - my) / fit.scale;
      return [x, y];
    },
    [],
  );

  /** Nearest corner name label to a mouse event, within a grab radius. */
  const labelAtEvent = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>): Row | null => {
      const fit = fitRef.current;
      const canvas = canvasRef.current;
      if (!fit || !canvas || !spline) return null;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const centroid = centroidOf(spline.pts);
      let best: Row | null = null;
      let bestD = 22 * 22; // grab radius in px²
      for (const r of rows) {
        if (!r.name) continue;
        const apexLocal = r.apex
          ? projectLocal(r.apex, spline.centre)
          : spline.pts[nearestByDistance(spline.distance, r.distance)];
        const labelLocal = r.label
          ? projectLocal(r.label, spline.centre)
          : outwardLabelPos(apexLocal, centroid);
        const lx = fit.ox + (labelLocal[0] - fit.minX) * fit.scale;
        const ly = fit.height - fit.oy - (labelLocal[1] - fit.minY) * fit.scale;
        const d = (lx - mx) ** 2 + (ly - my) ** 2;
        if (d < bestD) {
          bestD = d;
          best = r;
        }
      }
      return best;
    },
    [spline, rows],
  );

  function onCanvasMove(e: React.MouseEvent<HTMLCanvasElement>): void {
    const idx = indexAtEvent(e);
    setHoverIdx(idx);
    if (drag) {
      if (drag.kind === 'sector' && idx != null) {
        setSectorEdits((es) => es.map((s) => (s.id === drag.id ? { ...s, idx } : s)));
      } else if (drag.kind === 'label' && spline) {
        const local = localAtEvent(e);
        if (local) {
          const gps = unprojectLocal(local, spline.centre);
          setRows((rs) => rs.map((r) => (r.id === drag.id ? { ...r, label: gps } : r)));
        }
      }
      setCursor('grabbing');
      return;
    }
    // Show a grab cursor when hovering something movable.
    const hit = mode === 'sectors' ? gateAtEvent(e) : labelAtEvent(e);
    setCursor(hit ? 'grab' : 'crosshair');
  }

  function onCanvasDown(e: React.MouseEvent<HTMLCanvasElement>): void {
    if (mode === 'sectors') {
      const hit = gateAtEvent(e);
      if (hit) {
        setDrag({ kind: 'sector', id: hit.id });
        setCursor('grabbing');
        suppressClickRef.current = true; // don't add a new gate on the release click
      }
    } else {
      const hit = labelAtEvent(e);
      if (hit) {
        setDrag({ kind: 'label', id: hit.id });
        setCursor('grabbing');
        suppressClickRef.current = true; // don't start a corner pick on release
      }
    }
  }

  function onCanvasUp(): void {
    if (drag) {
      setDrag(null);
      setCursor('grab');
    }
  }

  function onCanvasClick(e: React.MouseEvent<HTMLCanvasElement>): void {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    const idx = indexAtEvent(e);
    if (idx == null) return;
    if (mode === 'sectors') {
      // Add a new sector gate at the clicked racing-line sample.
      setSectorEdits((es) => [...es, { id: makeId('sec'), idx }]);
      return;
    }
    if (!pending) {
      setPending({ startIdx: idx, endIdx: null });
    } else if (pending.endIdx == null) {
      setPending({ startIdx: pending.startIdx, endIdx: idx });
      setTimeout(() => nameInputRef.current?.focus(), 0);
    } else {
      // Both already set — start a fresh selection.
      setPending({ startIdx: idx, endIdx: null });
      setName('');
    }
  }

  function commitPending(): void {
    if (!pending || pending.endIdx == null || !spline) return;
    const sd = distAt(pending.startIdx);
    const ed = distAt(pending.endIdx);
    const startDistance = Math.round(Math.min(sd, ed));
    const endDistance = Math.round(Math.max(sd, ed));
    const distance = Math.round((startDistance + endDistance) / 2);
    // Anchor the corner to fixed physical GPS points so it never drifts between
    // laps: entry/exit at the clicked samples, apex at the span midpoint.
    const lo = Math.min(pending.startIdx, pending.endIdx);
    const hi = Math.max(pending.startIdx, pending.endIdx);
    const apexIdx = Math.round((lo + hi) / 2);
    const entry = unprojectLocal(spline.pts[lo], spline.centre);
    const exit = unprojectLocal(spline.pts[hi], spline.centre);
    const apexLocal = spline.pts[apexIdx];
    const apex = unprojectLocal(apexLocal, spline.centre);
    // Default label just outside the track so it never overlaps the racing line.
    const label = unprojectLocal(outwardLabelPos(apexLocal, centroidOf(spline.pts)), spline.centre);
    setRows((rs) => [
      ...rs,
      {
        id: makeId('cnr'),
        name: name.trim(),
        apex,
        entry,
        exit,
        label,
        distance,
        startDistance,
        endDistance,
      },
    ]);
    setPending(null);
    setName('');
  }

  function cancelPending(): void {
    setPending(null);
    setName('');
  }

  function removeRow(id: string): void {
    setRows((rs) => rs.filter((r) => r.id !== id));
  }

  function removeSector(id: string): void {
    setSectorEdits((es) => es.filter((s) => s.id !== id));
  }

  async function save(): Promise<void> {
    const cleaned: TrackCorner[] = rows
      .map((r) => ({
        id: r.id,
        name: r.name.trim(),
        distance: Math.max(0, Math.min(r.distance || 0, lapDistance || r.distance || 0)),
        ...(r.apex ? { apex: r.apex } : {}),
        ...(r.entry ? { entry: r.entry } : {}),
        ...(r.exit ? { exit: r.exit } : {}),
        ...(r.label ? { label: r.label } : {}),
        ...(r.startDistance != null ? { startDistance: r.startDistance } : {}),
        ...(r.endDistance != null ? { endDistance: r.endDistance } : {}),
      }))
      .filter((r) => r.name.length > 0)
      .sort((a, b) => a.distance - b.distance);

    // Derive physical GPS gate segments from the editable racing-line anchors so
    // every lap on this track is split at the same place. Names are assigned by
    // crossing order (S2, S3, …); S/F is the implicit lap boundary.
    const sectors: TrackGate[] = spline
      ? [...sectorEdits]
          .sort((a, b) => a.idx - b.idx)
          .map((se, i) => {
            const g = perpendicularGate(spline.pts, se.idx);
            return {
              id: se.id,
              name: `S${i + 2}`,
              a: unprojectLocal(g.a, spline.centre),
              b: unprojectLocal(g.b, spline.centre),
            };
          })
      : [];

    setSaving(true);
    await saveTrackConfig({ corners: cleaned, sectors });
    setSaving(false);
    props.onClose();
  }

  const step: 'entry' | 'exit' | 'name' = !pending
    ? 'entry'
    : pending.endIdx == null
      ? 'exit'
      : 'name';

  return (
    <div className="overlay" onClick={props.onClose}>
      <div className="dialog corner-editor" onClick={(e) => e.stopPropagation()}>
        <div className="stats-head">
          <div>
            <h2 style={{ margin: 0 }}>Track editor</h2>
            <div className="small muted">
              {track?.name ?? 'No track'}
              {lapDistance > 0 && ` · lap ${Math.round(lapDistance)} m`}
            </div>
          </div>
          <button className="icon-btn" onClick={props.onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {!trackId || !track || !referenceLap ? (
          <div className="small muted" style={{ padding: 16 }}>
            Select a reference lap first so a track is active.
          </div>
        ) : (
          <>
            <div className="track-editor-tabs">
              <button
                className={mode === 'corners' ? 'tab active' : 'tab'}
                onClick={() => setMode('corners')}
              >
                Corners
              </button>
              <button
                className={mode === 'sectors' ? 'tab active' : 'tab'}
                onClick={() => setMode('sectors')}
              >
                Sectors
              </button>
            </div>

            <div className="corner-map-wrap" ref={wrapRef}>
              {!spline ? (
                <div className="corner-map-loading small muted">Loading track…</div>
              ) : (
                <canvas
                  ref={canvasRef}
                  className="corner-map-canvas"
                  style={{ cursor }}
                  onMouseMove={onCanvasMove}
                  onMouseLeave={() => {
                    setHoverIdx(null);
                    if (drag) {
                      setDrag(null);
                    }
                    setCursor('crosshair');
                  }}
                  onMouseDown={onCanvasDown}
                  onMouseUp={onCanvasUp}
                  onClick={onCanvasClick}
                />
              )}
            </div>

            {mode === 'corners' ? (
              <>
                <div className="corner-picker-bar">
                  {step === 'entry' && (
                    <span className="small">
                      Click the <strong>corner entry</strong> on the racing line.
                    </span>
                  )}
                  {step === 'exit' && (
                    <span className="small">
                      Now click the <strong>corner exit</strong>.
                    </span>
                  )}
                  {step === 'name' && pending && (
                    <>
                      <span className="small muted">
                        {Math.round(distAt(pending.startIdx))}–
                        {Math.round(distAt(pending.endIdx ?? 0))} m
                      </span>
                      <input
                        ref={nameInputRef}
                        className="corner-name"
                        type="text"
                        placeholder="Corner name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && name.trim()) commitPending();
                          if (e.key === 'Escape') cancelPending();
                        }}
                      />
                      <button className="primary" disabled={!name.trim()} onClick={commitPending}>
                        Add corner
                      </button>
                    </>
                  )}
                  {pending && (
                    <button onClick={cancelPending}>
                      {step === 'name' ? 'Discard' : 'Cancel'}
                    </button>
                  )}
                </div>

                <div className="corner-list">
                  {rows.length === 0 && (
                    <div className="small muted" style={{ padding: '8px 0' }}>
                      No corners yet. Click the track above to add one.
                    </div>
                  )}
                  {[...rows]
                    .sort((a, b) => a.distance - b.distance)
                    .map((r, i) => (
                      <div className="corner-row" key={r.id}>
                        <span className="corner-index small muted">{i + 1}</span>
                        <input
                          className="corner-name"
                          type="text"
                          placeholder="Corner name"
                          value={r.name}
                          onChange={(e) =>
                            setRows((rs) =>
                              rs.map((x) => (x.id === r.id ? { ...x, name: e.target.value } : x)),
                            )
                          }
                        />
                        <span className="corner-dist small muted">
                          {r.startDistance != null && r.endDistance != null
                            ? `${r.startDistance}–${r.endDistance} m`
                            : `${Math.round(r.distance)} m`}
                        </span>
                        <button
                          className="danger small"
                          onClick={() => removeRow(r.id)}
                          aria-label="Remove corner"
                        >
                      ✕
                    </button>
                  </div>
                ))}
            </div>
              </>
            ) : (
              <>
                <div className="corner-picker-bar">
                  <span className="small">
                    Click the racing line to <strong>add a sector gate</strong>; drag a gate to
                    move it. Gates apply to every lap on this track.
                  </span>
                </div>

                <div className="corner-list">
                  {sectorEdits.length === 0 && (
                    <div className="small muted" style={{ padding: '8px 0' }}>
                      No sector gates yet — an even distance split is used. Click the track above to
                      define real gates.
                    </div>
                  )}
                  <div className="corner-row">
                    <span className="corner-index small muted">—</span>
                    <span className="small" style={{ flex: 1 }}>
                      S/F (start / finish)
                    </span>
                    <span className="corner-dist small muted">lap boundary</span>
                    <span style={{ width: 24 }} />
                  </div>
                  {[...sectorEdits]
                    .sort((a, b) => a.idx - b.idx)
                    .map((se, i) => (
                      <div className="corner-row" key={se.id}>
                        <span className="corner-index small muted">{i + 2}</span>
                        <span className="small" style={{ flex: 1 }}>
                          Sector gate S{i + 2}
                        </span>
                        <span className="corner-dist small muted">
                          {spline ? `${Math.round(distAt(se.idx))} m` : ''}
                        </span>
                        <button
                          className="danger small"
                          onClick={() => removeSector(se.id)}
                          aria-label="Remove sector gate"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                </div>
              </>
            )}

            <div className="corner-actions">
              <div className="spacer" style={{ flex: 1 }} />
              <button onClick={props.onClose}>Cancel</button>
              <button className="primary" disabled={saving} onClick={() => void save()}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
